import { computed } from '@ember/object';
import { isEmpty, isBlank } from '@ember/utils';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import * as JSONBig from 'json-bigint';
import { belongsTo } from 'ember-data/relationships';
import DS from 'ember-data';
import { inject as service } from '@ember/service';
import actionInfo from 'cio/utils/action-info';
import CustomerModel from 'cio/models/customer';
import DeliveryModel from 'cio/models/delivery';
import { ActionType } from 'cio/models/action';
import ObjectModel from 'cio/models/object';
import ObjectTypeModel from 'cio/models/object-type';
import EnvironmentService from 'cio/services/environment';
import {
  IdentifierKey,
  isAttributeEnabledIdentifier,
  isObjectAttributeIdentifier,
  ObjectIdentifierKey,
} from 'cio/utils/identifiers';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';
import pluralize from 'cio/utils/pluralize';
import { ActivityLogType } from 'cio/utils/activity-log-types';
import { ActivityLogSourceSubType, ActivityLogSourceType } from 'cio/utils/activity-log-source-types';
import { ActivityLogSource } from 'cio/utils/activity-logs';
import isDestroyed from 'cio/utils/is-destroyed';

export default class LogModel extends Model {
  @service environment!: EnvironmentService;

  @attr('string') type!: ActivityLogType;
  @attr('string') name!: string;
  @attr('number') timestamp!: number;
  @attr('number') processedAt!: number;
  @attr() attrs!: Record<string, unknown> | unknown[]; // JSON object or array (for webhook-triggered payloads)
  @attr('string') deliveryType!: string;
  @attr('string') customerId!: string;
  @attr('string') oldCustomerId!: string;
  @attr('string') oldObjectId!: string;

  @belongsTo('customer', { async: true, inverse: null }) customer!: DS.PromiseObject<CustomerModel>;
  @belongsTo('delivery', { async: true, inverse: null }) delivery!: DeliveryModel;
  @belongsTo('object', { async: true, inverse: null }) object!: DS.PromiseObject<ObjectModel>;
  @belongsTo('object-type', { async: true, inverse: 'logs' }) objectType!: DS.PromiseObject<ObjectTypeModel>;

  @computed('type')
  get parsedType(): ActivityLogType {
    // Objects log type should return common pattern `"_o:*:{type}"`
    // Relationships log type should return common pattern `"_r:*:{type}"`
    // cast to "string" to access the ".replace" method, then cast back for TS
    return (this.type as string)?.replace(/(_(o|r)):[^:]+:/, '$1:*:') as ActivityLogType;
  }

  @computed('name', 'parsedType', 'attributeChangeName', 'profileCreateName', 'profileDeleteName', 'profileMergeName')
  get displayName() {
    if (this.isAttributeChange || this.isFailedAttributeChange) {
      return this.attributeChangeName;
    }

    if ([ActivityLogType.ProfileCreate, ActivityLogType.ObjectCreate].includes(this.parsedType)) {
      return this.profileCreateName;
    }

    if (this.parsedType === ActivityLogType.ProfileDelete) {
      return this.profileDeleteName;
    }
    if (this.parsedType === ActivityLogType.ObjectDelete) {
      return 'Deletion';
    }

    if (this.parsedType === ActivityLogType.ProfileMerge) {
      return this.profileMergeName;
    }

    return this.name;
  }

  @attr('camel-case', { defaultValue: () => [] }) sources!: ActivityLogSource[];

  get filteredSources() {
    return this.sources.filter((source) => {
      if (source.type !== ActivityLogSourceType.Action) {
        return true;
      }

      // At the moment, newsletter and transactional message activity log
      // sources don't contain the right information.
      // This is a temporary workaround to hide broken sources.
      return !this.name.includes('newsletter') && !this.name.includes('transactional');
    });
  }

  @computed('deliveryType')
  get deliveryTypeName() {
    let actionType = `${this.deliveryType}_action` as ActionType;
    return actionInfo(actionType).name;
  }

  @computed('type')
  get hasDraft() {
    return this.type.includes('drafted_');
  }

  @computed('type')
  get isFailedEvent() {
    return this.type === ActivityLogType.FailedEvent;
  }

  get isPeopleMergeEvent() {
    return this.sources.any((s) => s.type === ActivityLogSourceType.ProfileMerge);
  }

  get isAnonymousMergeEvent() {
    if (this.type === ActivityLogType.AnonMerge) {
      return false;
    }
    return this.sources.any((source) => !isEmpty(source.anonymousId));
  }

  get anonymousId() {
    let source = this.sources.find((source) => !isEmpty(source.anonymousId));
    return source?.anonymousId;
  }

  @computed('parsedType')
  get isAttributeChange() {
    return [ActivityLogType.AttributeChange, ActivityLogType.ObjectAttributeChange].includes(this.parsedType);
  }

  @computed('parsedType')
  get isFailedAttributeChange() {
    return [ActivityLogType.FailedAttributeChange, ActivityLogType.ObjectFailedAttributeChange].includes(
      this.parsedType,
    );
  }

  @computed('attrs')
  get attrsAsJsonString() {
    return JSONBig.stringify(this.attrs, null, '  ');
  }

  get isObject() {
    if (isDestroyed(this)) {
      return false;
    }

    return !isEmpty(this.belongsTo('object' as any).id()) || this.oldObjectId != null;
  }

  @computed('isAttributeChange', 'isFailedAttributeChange', 'name', 'attrs')
  get attributeChangeName() {
    let { isAttributeChange, isFailedAttributeChange, name, attrs } = this;

    if (!isAttributeChange && !isFailedAttributeChange) {
      // eslint-disable-next-line getter-return
      return;
    }

    // Support old-style attribute changes with embedded name
    if (!isBlank(name)) {
      return name;
    }

    // If one attribute changed or failed to change, return its name
    let names = Object.keys(attrs);
    let identifierAttributes = names.filter((name) => {
      if (this.isObject) {
        return isObjectAttributeIdentifier(name);
      }
      return isAttributeEnabledIdentifier(name, this.environment.current.identifiers);
    });
    let identifierMessage = identifierAttributes.length
      ? ` (${formatNumberAndPluralize([identifierAttributes.length, 'identifier'])})`
      : '';

    if (names.length === 1) {
      return `${names[0]}${identifierAttributes.length ? ' (identifier)' : ''}`;
    }

    return `${names.length} attributes${identifierMessage} ${isFailedAttributeChange ? 'failed to change' : 'changed'}`;
  }

  @computed('parsedType', 'name', 'attrs')
  get profileCreateName() {
    if (![ActivityLogType.ProfileCreate, ActivityLogType.ObjectCreate].includes(this.parsedType)) {
      return;
    }

    // If one attribute changed or failed to change, return its name
    let names = Object.keys(this.attrs);
    let identifierAttributes = names.filter(
      (name) =>
        (isAttributeEnabledIdentifier(name, this.environment.current.identifiers) && name !== IdentifierKey.CioId) ||
        name === ObjectIdentifierKey.ObjectId,
    );

    return `${identifierAttributes.join(', ')} (${pluralize(identifierAttributes.length, 'identifier')})`;
  }

  get profileDeleteName() {
    if (this.type !== ActivityLogType.ProfileDelete) {
      return;
    }

    if (this.sources.any((s) => s.type === ActivityLogSourceType.UI)) {
      return 'Manual deletion';
    }

    if (this.sources.any((s) => s.siteId != null)) {
      return 'API deletion';
    }

    return 'Deletion';
  }

  get profileMergeName() {
    if (this.type !== ActivityLogType.ProfileMerge) {
      return;
    }

    if (this.sources.any((s) => s.type === ActivityLogSourceType.UI)) {
      return 'Manual merge';
    }

    if (this.sources.any((s) => s.subtype === ActivityLogSourceSubType.AutoMerge)) {
      return 'Multi-identifier merge';
    }

    if (this.sources.any((s) => s.siteId != null)) {
      return 'API merge';
    }

    return 'Manual merge';
  }

  get webhookLogCampaign() {
    if (this.type !== ActivityLogType.WebhookEvent) {
      return null;
    }

    let source = this.sources.find((s) => s.campaignId != null && s.campaignName != null);

    if (source == null) {
      return null;
    }

    return {
      id: source.campaignId,
      name: source.campaignName,
    };
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    log: LogModel;
  }
}
